import React, { useState } from "react";
import { Truck, ArrowRight, Info, DollarSign, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { useQuoteModal } from "@/hooks/useQuoteModal";

interface LoadTier {
  id: string;
  label: string;
  fillPercent: number;
  minPrice: number;
  maxPrice: number;
  description: string;
  examples: string[];
}

const LOAD_TIERS: LoadTier[] = [
  {
    id: "single",
    label: "Single Item",
    fillPercent: 12,
    minPrice: 99,
    maxPrice: 149,
    description: "One heavy item hauled away",
    examples: ["Couch or loveseat", "Refrigerator", "Mattress set", "TV or desk"],
  },
  {
    id: "quarter",
    label: "1/4 Truckload",
    fillPercent: 30,
    minPrice: 199,
    maxPrice: 299,
    description: "A few bulky items or small pile",
    examples: ["3-5 furniture pieces", "Pile of boxes", "Old appliances", "Yard debris pile"],
  },
  {
    id: "half",
    label: "1/2 Truckload",
    fillPercent: 55,
    minPrice: 349,
    maxPrice: 449,
    description: "Several rooms of junk",
    examples: ["Living room + bedroom", "Garage corner", "Office furniture", "Construction debris"],
  },
  {
    id: "three-quarter",
    label: "3/4 Truckload",
    fillPercent: 78,
    minPrice: 499,
    maxPrice: 599,
    description: "Heavy cleanout, multiple rooms",
    examples: ["2-3 room cleanout", "Full garage", "Storage unit", "Renovation waste"],
  },
  {
    id: "full",
    label: "Full Truckload",
    fillPercent: 100,
    minPrice: 649,
    maxPrice: 799,
    description: "Complete property cleanout",
    examples: ["Whole house cleanout", "Estate cleanout", "Commercial clearing", "Large construction site"],
  },
];

export const PriceEstimatorSection: React.FC = () => {
  const [selectedTier, setSelectedTier] = useState(2); // Default to 1/2 truckload
  const { openModal } = useQuoteModal();
  const tier = LOAD_TIERS[selectedTier];

  return (
    <section id="pricing" className="py-20 bg-white text-slate-950 relative overflow-hidden">
      {/* Decorative glows */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 bg-amber-500/30 border border-amber-500/60 text-amber-900 font-extrabold px-4 py-1.5 rounded-full text-xs uppercase tracking-widest mb-4">
            <DollarSign className="w-4 h-4" /> Instant Price Estimator
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black font-heading uppercase tracking-tight mb-4">
            Junk Removal <span className="text-amber-600">Prices</span> & <span className="text-primary">Estimates</span>
          </h2>
          <p className="text-lg text-slate-600 font-medium">
            Drag the slider to match your junk volume and see our upfront junk removal prices in seconds. Final pricing is confirmed on-site with no hidden fees — serving Davie, Fort Lauderdale, Hollywood, Plantation, Cooper City & Sunrise, FL.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          {/* LEFT: Visual Truck + Slider */}
            <div className="bg-slate-900/80 backdrop-blur-sm border-2 border-amber-400/30 shadow-[0_0_60px_rgba(251,191,36,0.15)] rounded-3xl p-6 sm:p-8 flex flex-col relative overflow-hidden">
              {/* Inner corner glow */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-bl-full blur-2xl pointer-events-none" />
            {/* Truck SVG visual */}
            <div className="flex-1 flex items-center justify-center mb-6">
              <TruckVisual fillPercent={tier.fillPercent} />
            </div>

            {/* Slider */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-black uppercase tracking-widest text-slate-500">Junk Volume</span>
                <span className="text-sm font-black text-amber-400">{tier.label}</span>
              </div>
              <input
                type="range"
                min={0}
                max={LOAD_TIERS.length - 1}
                step={1}
                value={selectedTier}
                onChange={(e) => setSelectedTier(Number(e.target.value))}
                className="price-slider w-full"
                aria-label="Select junk volume"
              />
              <div className="flex justify-between text-[8px] sm:text-[10px] font-bold uppercase tracking-wider text-slate-400 gap-1">
                {LOAD_TIERS.map((t) => (
                  <span key={t.id} className={`text-center leading-tight ${selectedTier === LOAD_TIERS.indexOf(t) ? "text-amber-400" : ""}`}>
                    {t.label.replace(" Truckload", "").replace("Single ", "1 Item")}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT: Price Card */}
            <div className="bg-gradient-to-br from-white to-slate-50 border-2 border-amber-400/60 shadow-lg rounded-3xl p-6 sm:p-8 flex flex-col">
            <div className="flex items-center gap-2 mb-1">
              <Truck className="w-5 h-5 text-primary" />
              <span className="text-xs font-black uppercase tracking-widest text-slate-500">
                Estimated Range
              </span>
            </div>

            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-5xl sm:text-6xl font-black font-heading text-amber-500">
                ${tier.minPrice}
              </span>
              <span className="text-2xl font-bold text-slate-400">–</span>
              <span className="text-4xl font-black font-heading text-slate-950">
                ${tier.maxPrice}
              </span>
            </div>

            <p className="text-slate-600 font-bold mb-6">
              {tier.description}
            </p>

            {/* What fits */}
            <div className="mb-6">
              <span className="text-xs font-black uppercase tracking-widest text-slate-500 mb-3 block">
                Typically Fits
              </span>
              <div className="grid grid-cols-2 gap-2">
                {tier.examples.map((ex) => (
                  <div key={ex} className="flex items-center gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                    <span>{ex}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Disclaimer */}
            <div className="flex items-start gap-2 bg-slate-100 border border-slate-200 rounded-xl p-3 mb-6">
              <Info className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-xs text-slate-500 leading-relaxed">
                Estimates are based on volume only. Final price may vary with weight, stairs, distance, and disposal fees. Free on-site quotes always available.
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 mt-auto">
              <Button size="lg" onClick={openModal} className="w-full !bg-rose-600 hover:!bg-rose-700 text-white shadow-lg shadow-rose-500/30 font-black uppercase tracking-wider border-0">
                  Online Free Quote
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              <a href="tel:9542808090" className="w-full sm:w-auto">
                <Button size="lg" className="w-full bg-amber-400 hover:bg-amber-500 text-slate-950 font-black uppercase tracking-wider">
                  Call (954) 280-8090
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .price-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 8px;
          border-radius: 9999px;
          background: linear-gradient(to right, #f59e0b, #e11d48);
          outline: none;
          cursor: pointer;
        }
        .price-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: #fff;
          border: 4px solid #e11d48;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0,0,0,0.4);
          transition: transform 0.15s ease;
        }
        .price-slider::-webkit-slider-thumb:hover {
          transform: scale(1.15);
        }
        .price-slider::-moz-range-thumb {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: #fff;
          border: 4px solid #e11d48;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0,0,0,0.4);
        }
      `}</style>
    </section>
  );
};

/* Visual truck that fills based on load percentage */
const TruckVisual: React.FC<{ fillPercent: number }> = ({ fillPercent }) => {
  const clampedFill = Math.max(8, Math.min(100, fillPercent));
  return (
    <svg viewBox="0 0 400 220" className="w-full max-w-md drop-shadow-2xl" role="img" aria-label="Junk truck fill level">
      {/* Truck body container */}
      <defs>
        <clipPath id="truckBox">
          <rect x="40" y="50" width="200" height="110" rx="6" />
        </clipPath>
        <linearGradient id="junkGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f59e0b" />
          <stop offset="100%" stopColor="#e11d48" />
        </linearGradient>
      </defs>

      {/* Cab */}
      <g>
        <rect x="240" y="70" width="120" height="90" rx="10" fill="#334155" stroke="#f59e0b" strokeWidth="3" />
        <rect x="250" y="80" width="100" height="40" rx="6" fill="#0f172a" stroke="#334155" strokeWidth="2" />
        <rect x="258" y="86" width="84" height="28" rx="4" fill="#1e293b" />
        {/* Headlight */}
        <circle cx="350" cy="140" r="6" fill="#fbbf24" />
      </g>

      {/* Truck box (dump bed) */}
      <rect x="40" y="50" width="200" height="110" rx="6" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="3" />

      {/* Junk fill inside box (clipped) */}
      <g clipPath="url(#truckBox)">
        {/* Base fill */}
        <rect
          x="40"
          y={50 + 110 * (1 - clampedFill / 100)}
          width="200"
          height={110 * (clampedFill / 100)}
          fill="url(#junkGrad)"
          opacity="0.9"
        />
        {/* Junk texture bumps */}
        {[...Array(8)].map((_, i) => {
          const bumpY = 50 + 110 * (1 - clampedFill / 100) - (i % 2 === 0 ? 8 : 4);
          const bumpX = 50 + i * 24;
          if (bumpY < 50) return null;
          return (
            <circle
              key={i}
              cx={bumpX}
              cy={bumpY}
              r={6 + (i % 3)}
              fill={i % 2 === 0 ? "#fbbf24" : "#e11d48"}
              opacity="0.7"
            />
          );
        })}
      </g>

      {/* Box top rim */}
      <rect x="40" y="50" width="200" height="6" rx="3" fill="#f59e0b" />

      {/* Wheels */}
      <circle cx="90" cy="165" r="22" fill="#0f172a" stroke="#475569" strokeWidth="4" />
      <circle cx="90" cy="165" r="10" fill="#334155" />
      <circle cx="200" cy="165" r="22" fill="#0f172a" stroke="#475569" strokeWidth="4" />
      <circle cx="200" cy="165" r="10" fill="#334155" />
      <circle cx="320" cy="165" r="22" fill="#0f172a" stroke="#475569" strokeWidth="4" />
      <circle cx="320" cy="165" r="10" fill="#334155" />

      {/* Ground line */}
      <line x1="20" y1="195" x2="380" y2="195" stroke="#334155" strokeWidth="2" strokeDasharray="6 6" />

      {/* Percentage label */}
      <text x="140" y="40" textAnchor="middle" fontSize="16" fontWeight="900" fill="#e11d48" fontFamily="system-ui, sans-serif">
        {clampedFill}% FULL
      </text>
    </svg>
  );
};
